/**
 * Lightweight hook that counts pending teacher actions
 * Uses count-only queries (HEAD) for performance
 * Provides data for sidebar badges, dashboard action center, and collab tab badges
 */
import { useState, useEffect, useCallback } from 'react'
import { supabase, isDemoMode } from '@/lib/supabase'
import { useAuthContext } from '@/contexts/AuthContext'
import { isTeacherRole } from '@/utils/helpers'
import { useChatUnread } from '@/hooks/useChat'

export interface TeacherPendingActions {
  totalPending: number
  pendingAvailability: number
  pendingAssignments: number
  pendingReplacements: number
  pendingReschedules: number
  unreadMessages: number
  isLoading: boolean
}

const ZERO: TeacherPendingActions = {
  totalPending: 0, pendingAvailability: 0, pendingAssignments: 0,
  pendingReplacements: 0, pendingReschedules: 0, unreadMessages: 0, isLoading: false,
}

const DEMO: TeacherPendingActions = {
  totalPending: 3, pendingAvailability: 1, pendingAssignments: 1,
  pendingReplacements: 1, pendingReschedules: 0, unreadMessages: 2, isLoading: false,
}

export function useTeacherPendingActions(): TeacherPendingActions {
  const { user } = useAuthContext()
  const chatUnread = useChatUnread()
  const [counts, setCounts] = useState(ZERO)
  const [isLoading, setIsLoading] = useState(false)

  const isTeacher = isTeacherRole(user?.role)

  const fetchCounts = useCallback(async () => {
    if (isDemoMode) { setCounts(DEMO); return }
    if (!user || !isTeacher) { setCounts(ZERO); return }

    setIsLoading(true)
    try {
      // All count queries in parallel
      const [assignRes, replaceRes, rescheduleRes, subjectsRes] = await Promise.all([
        // Pending assignments
        supabase.from('session_assignments')
          .select('id', { count: 'exact', head: true })
          .eq('teacher_id', user.id)
          .eq('status', 'pending'),
        // Pending replacement candidacies
        supabase.from('replacement_candidates')
          .select('id', { count: 'exact', head: true })
          .eq('teacher_id', user.id)
          .eq('status', 'pending'),
        // My pending reschedule requests (informational)
        supabase.from('session_change_requests')
          .select('id', { count: 'exact', head: true })
          .eq('requested_by', user.id)
          .eq('teacher_id', user.id)
          .eq('status', 'pending'),
        // My subjects (for availability request counting)
        supabase.from('teacher_subjects')
          .select('subject_id')
          .eq('teacher_id', user.id),
      ])

      const pendingAssignments = assignRes.count || 0
      const pendingReplacements = replaceRes.count || 0
      const pendingReschedules = rescheduleRes.count || 0

      // Count open availability requests for my subjects that I haven't responded to
      let pendingAvailability = 0
      const subjectIds = (subjectsRes.data || []).map(s => s.subject_id).filter(Boolean)

      if (subjectIds.length > 0) {
        const [openRequestsRes, myResponsesRes] = await Promise.all([
          supabase.from('availability_requests')
            .select('id')
            .eq('status', 'open')
            .in('subject_id', subjectIds),
          supabase.from('availability_request_responses')
            .select('request_id')
            .eq('teacher_id', user.id),
        ])

        const openRequestIds = new Set((openRequestsRes.data || []).map(r => r.id))
        const respondedIds = new Set((myResponsesRes.data || []).map(r => r.request_id))
        pendingAvailability = [...openRequestIds].filter(id => !respondedIds.has(id)).length
      }

      const totalPending = pendingAvailability + pendingAssignments + pendingReplacements

      setCounts({
        totalPending,
        pendingAvailability,
        pendingAssignments,
        pendingReplacements,
        pendingReschedules,
        unreadMessages: chatUnread,
        isLoading: false,
      })
    } catch (err) {
      console.error('useTeacherPendingActions error:', err)
    } finally {
      setIsLoading(false)
    }
  }, [user, isTeacher, chatUnread])

  // Initial fetch + periodic refresh
  useEffect(() => {
    if (!isTeacher || isDemoMode) return
    fetchCounts()
    const interval = setInterval(fetchCounts, 60_000)
    return () => clearInterval(interval)
  }, [fetchCounts, isTeacher])

  // Realtime subscriptions
  useEffect(() => {
    if (!isTeacher || isDemoMode || !user) return

    const channel = supabase
      .channel(`teacher-pending-actions:${Math.random().toString(36).slice(2, 10)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'session_assignments' }, () => fetchCounts())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'replacement_candidates' }, () => fetchCounts())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'availability_requests' }, () => fetchCounts())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'availability_request_responses' }, () => fetchCounts())
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [isTeacher, user, fetchCounts])

  if (isDemoMode) return { ...DEMO, unreadMessages: chatUnread }
  if (!isTeacher) return ZERO

  return { ...counts, unreadMessages: chatUnread, isLoading }
}
